// ============================================================
// TELEMETRY STREAM — real-time WebSocket abstraction
// ============================================================
// Two backends behind one interface:
//
//   subscribe(deviceId, onTick) → unsubscribe()
//
// Mock mode (default in dev / demo):
//   Synthesizes ticks every TICK_INTERVAL_MS using the last
//   known telemetry as a base + realistic jitter. No network.
//
// Real mode (when VITE_WS_BASE is set):
//   Opens one WebSocket to the API Gateway WebSocket endpoint,
//   authenticates with the Cognito access token, fans out to
//   subscribers per deviceId. Auto-reconnects with exponential
//   backoff and resubscribes on reconnect.
//
// The page-level UX is identical in both modes: a hook gets a
// new value every ~1s and re-renders the chart.

import { getTelemetry } from './starlink-api.js';
import { captureError } from './sentry.js';
import { API_CONFIG } from './runtime-mode.js';

const TICK_INTERVAL_MS = 1500;          // mock: how often we synthesize a new sample
const RECONNECT_BASE_MS = 1000;          // real: starting backoff
const RECONNECT_MAX_MS = 30000;          // real: cap
const HEARTBEAT_MS = 25000;              // real: ping cadence so API GW doesn't 10-min timeout
const TOKEN_REFRESH_MS = 50 * 60 * 1000; // real: soft-reconnect every 50min to pick up fresh Cognito token

const WS_BASE = import.meta.env?.VITE_WS_BASE;
const USE_MOCK = !WS_BASE;

// A public/guest demo must never open a real WebSocket — synthesize ticks
// locally instead. Reads the demo-session flag directly (same key as
// starlink-api.js) to avoid an import cycle. Checked per-call so starting a
// demo doesn't require a reload.
function inDemoSession() {
  try {
    return typeof sessionStorage !== 'undefined'
      && !!sessionStorage.getItem('netcom_connect_demo_customer_session');
  } catch { return false; }
}
function wantsMock() { return USE_MOCK || inDemoSession(); }

// The anonymized REAL-data demo is a NORMAL authenticated session (not the
// sessionStorage guest-demo). It must NOT synthesize fake ticks (that would
// contradict "real data") and must NOT open a WebSocket for its fake account
// (which carries no data). Its live numbers come from the REST history store
// (getTelemetryHistory). Account id hardcoded to avoid an import cycle — it
// mirrors DEMO_ACCOUNT_NUMBER in src/config/demo.js.
function inRealDemoSession() {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem('netcom_connect_session');
    return !!raw && JSON.parse(raw)?.accountNumber === 'DEMO-HPS-001';
  } catch { return false; }
}

// ─── Subscriber bookkeeping ────────────────────────────────
// deviceId → Set<callback>
const subscribers = new Map();
// deviceId → mock interval id (mock mode only)
const mockTimers = new Map();
// deviceId → last seen telemetry payload (used for mock jitter base)
const lastSeen = new Map();

let socket = null;
let socketState = 'closed';              // closed | connecting | open
let reconnectAttempt = 0;
let heartbeatTimer = null;
let tokenRefreshTimer = null;
let getAccessToken = () => null;         // injected by initTelemetryStream

/**
 * Initialize the stream. Pass a function that returns the current
 * Cognito access token (or null in demo mode). Called once at app boot.
 */
export function initTelemetryStream({ getToken } = {}) {
  if (typeof getToken === 'function') getAccessToken = getToken;
}

/**
 * Subscribe to live telemetry for a single device.
 * @param {string} deviceId
 * @param {(payload: object) => void} onTick
 * @returns {() => void} unsubscribe
 */
export function subscribe(deviceId, onTick) {
  if (!deviceId || typeof onTick !== 'function') return () => {};

  let set = subscribers.get(deviceId);
  if (!set) {
    set = new Set();
    subscribers.set(deviceId, set);
  }
  set.add(onTick);

  if (inRealDemoSession()) {
    // No live WS + no synthetic ticks for the anonymized demo — the real
    // (aliased) numbers come from the REST history store instead.
  } else if (wantsMock()) {
    startMockTicker(deviceId);
  } else {
    ensureSocket();
    sendSubscribe(deviceId);
  }

  return () => unsubscribe(deviceId, onTick);
}

function unsubscribe(deviceId, onTick) {
  const set = subscribers.get(deviceId);
  if (!set) return;
  set.delete(onTick);
  if (set.size > 0) return;

  subscribers.delete(deviceId);
  if (wantsMock()) stopMockTicker(deviceId);
  else sendUnsubscribe(deviceId);
}

function fanOut(deviceId, payload) {
  const set = subscribers.get(deviceId);
  if (!set) return;
  for (const cb of set) {
    try { cb(payload); } catch (e) { captureError(e, { scope: 'telemetry-stream:fanOut', deviceId }); }
  }
}

// ─── Mock backend ─────────────────────────────────────────
// Fallback seed used when getTelemetry() fails (e.g., unknown deviceId in
// dev). Without this the ticker emits nothing forever — the "LIVE" badge
// is on but no data ever arrives. The audit caught this; the fallback
// makes the mock always produce SOMETHING.
const FALLBACK_SEED = {
  popPingLatencyMs: 38,
  downlinkThroughputMbps: 180,
  uplinkThroughputMbps: 14,
  signalQualityPercent: 96,
  obstructionFractionPercent: 0.4,
  snrDb: 11,
  softwareVersion: 'unknown',
  history24h: [],
  offline: false,
};

async function startMockTicker(deviceId) {
  if (mockTimers.has(deviceId)) return;
  // Seed from the static API so the first tick has sensible numbers
  try {
    const seed = await getTelemetry(deviceId);
    if (seed) lastSeen.set(deviceId, seed);
    else lastSeen.set(deviceId, { deviceId, ...FALLBACK_SEED, fetchedAt: new Date().toISOString() });
  } catch (e) {
    captureError(e, { scope: 'telemetry-stream:seed', deviceId });
    // Don't leave the ticker silent — use the fallback so the page still updates
    lastSeen.set(deviceId, { deviceId, ...FALLBACK_SEED, fetchedAt: new Date().toISOString() });
  }
  const id = setInterval(() => emitMockTick(deviceId), TICK_INTERVAL_MS);
  mockTimers.set(deviceId, id);
  // Emit one immediately so the chart isn't empty for 1.5s
  emitMockTick(deviceId);
}

function stopMockTicker(deviceId) {
  const id = mockTimers.get(deviceId);
  if (id) clearInterval(id);
  mockTimers.delete(deviceId);
  lastSeen.delete(deviceId);
}

function emitMockTick(deviceId) {
  const base = lastSeen.get(deviceId);
  if (!base) return;
  if (base.offline) {
    fanOut(deviceId, { ...base, fetchedAt: new Date().toISOString(), live: true });
    return;
  }
  // Random walk with mean reversion so the line doesn't drift to infinity
  const jitter = (cur, scale, min, max) => {
    const noise = (Math.random() - 0.5) * scale;
    const next = cur + noise - (cur - (min + max) / 2) * 0.05;
    return Math.max(min, Math.min(max, next));
  };
  const next = {
    deviceId,
    fetchedAt: new Date().toISOString(),
    live: true,
    popPingLatencyMs: jitter(base.popPingLatencyMs ?? 38, 4, 18, 90),
    downlinkThroughputMbps: jitter(base.downlinkThroughputMbps ?? 180, 12, 40, 320),
    uplinkThroughputMbps: jitter(base.uplinkThroughputMbps ?? 14, 1.5, 3, 32),
    signalQualityPercent: jitter(base.signalQualityPercent ?? 96, 1.5, 60, 100),
    obstructionFractionPercent: jitter(base.obstructionFractionPercent ?? 0.4, 0.2, 0, 8),
    snrDb: jitter(base.snrDb ?? 11, 0.3, 4, 16),
    softwareVersion: base.softwareVersion,
    history24h: base.history24h,
  };
  lastSeen.set(deviceId, next);
  fanOut(deviceId, next);
}

// ─── Real backend (WebSocket) ─────────────────────────────
async function fetchTicket() {
  // POST /ws/ticket with the Cognito access token in the Authorization header.
  // Returns a short-lived (30s) single-use ticket. The ticket is what we put
  // in the WebSocket URL — NOT the long-lived access token. This keeps the
  // access token out of CloudFront / API Gateway access logs and browser
  // history.
  const token = getAccessToken?.();
  if (!token) throw new Error('No access token available for ticket exchange');
  const base = API_CONFIG.base?.replace(/\/$/, '') || '';
  const res = await fetch(`${base}/ws/ticket`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) throw new Error(`Ticket exchange failed: ${res.status}`);
  const body = await res.json();
  if (!body?.ticket) throw new Error('Ticket exchange returned empty');
  return body.ticket;
}

async function ensureSocket() {
  if (socketState !== 'closed') return;
  socketState = 'connecting';
  try {
    const ticket = await fetchTicket();
    const url = `${WS_BASE}?ticket=${encodeURIComponent(ticket)}`;
    socket = new WebSocket(url);
    socket.onopen = onOpen;
    socket.onmessage = onMessage;
    socket.onerror = onError;
    socket.onclose = onClose;
  } catch (e) {
    socketState = 'closed';
    captureError(e, { scope: 'telemetry-stream:ensureSocket' });
    scheduleReconnect();
  }
}

function onOpen() {
  socketState = 'open';
  reconnectAttempt = 0;
  // Resubscribe to every active device after reconnect
  for (const deviceId of subscribers.keys()) sendSubscribe(deviceId);
  heartbeatTimer = setInterval(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      try { socket.send(JSON.stringify({ action: 'ping' })); } catch { /* noop */ }
    }
  }, HEARTBEAT_MS);
  // Cognito access tokens expire after 60min. Soft-reconnect every 50min so
  // we pick up a freshly-refreshed token instead of dying silently at the
  // 1-hour mark when API Gateway revalidates.
  tokenRefreshTimer = setTimeout(() => {
    if (socket) try { socket.close(1000, 'token-refresh'); } catch { /* noop */ }
  }, TOKEN_REFRESH_MS);
}

function onMessage(ev) {
  let msg;
  try { msg = JSON.parse(ev.data); }
  catch { return; }
  if (msg?.type === 'telemetry' && msg.deviceId) {
    fanOut(msg.deviceId, { ...msg.payload, deviceId: msg.deviceId, live: true });
  }
}

function onError(e) {
  captureError(new Error('WebSocket error'), { scope: 'telemetry-stream:onError', readyState: socket?.readyState });
}

function onClose() {
  socketState = 'closed';
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
  if (tokenRefreshTimer) { clearTimeout(tokenRefreshTimer); tokenRefreshTimer = null; }
  socket = null;
  if (subscribers.size > 0) scheduleReconnect();
}

/**
 * Cleanly close the stream — call on sign-out so the WebSocket (which may
 * still hold a now-revoked access token's session) is torn down rather
 * than leaking until the tab unloads.
 */
export function closeTelemetryStream() {
  for (const id of mockTimers.values()) clearInterval(id);
  mockTimers.clear();
  subscribers.clear();
  lastSeen.clear();
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
  if (tokenRefreshTimer) { clearTimeout(tokenRefreshTimer); tokenRefreshTimer = null; }
  if (socket) {
    try { socket.close(1000, 'sign-out'); } catch { /* noop */ }
    socket = null;
  }
  socketState = 'closed';
  reconnectAttempt = 0;
}

function scheduleReconnect() {
  const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * Math.pow(2, reconnectAttempt));
  reconnectAttempt += 1;
  setTimeout(() => ensureSocket(), delay);
}

function sendSubscribe(deviceId) {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  try { socket.send(JSON.stringify({ action: 'subscribe', deviceId })); }
  catch (e) { captureError(e, { scope: 'telemetry-stream:sendSubscribe', deviceId }); }
}

function sendUnsubscribe(deviceId) {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  try { socket.send(JSON.stringify({ action: 'unsubscribe', deviceId })); }
  catch { /* noop */ }
}

// ─── Diagnostic helpers ───────────────────────────────────
export function getStreamInfo() {
  return {
    mode: USE_MOCK ? 'mock' : 'websocket',
    socketState,
    activeSubscriptions: subscribers.size,
    reconnectAttempt,
  };
}
